import { Link } from "react-router-dom";
import { getRiotAssetsContext } from "@outplayed/riot-assets";
import { Icon } from "@ugitgud/legos/ui/icon/Icon";
import { SvgUggPlusOverlay } from "@ugitgud/legos/ui/icon/SVG/UggPlusOverlay";
import { getWinRateColor } from "@ugg/shared/utils/win-rates";
import { LPGainArrows } from "@ugg/shared/components/SummonerProfiles/overview/lp/LPGainArrows";
import { getTierColor, getTierColorHex } from "@ugg/shared/utils/tier-helpers";
import { SvgArrowForward } from "@ugitgud/legos/ui/icon/SVG/ArrowForward";
import { AppRoutes } from "@ugg/shared/routes/app-routes";

export interface PersonalizedBuildStats {
  summonerName: string;
  tagLine: string;
  region: string;
  iconId: number;
  lp: number;
  games: number;
  tier: string;
  winRate: number;
  pickRate: number;
  kda: number;
}

interface PersonalizedBuildCardProps {
  stats: PersonalizedBuildStats;
}

export function PersonalizedBuildCard({ stats }: PersonalizedBuildCardProps) {
  const { getProfileIconImg } = getRiotAssetsContext();

  const noData = stats.games < 3;
  const statValues = [
    { label: "KDA", value: stats.kda.toFixed(2), color: undefined },
    {
      label: "Win Rate",
      value: `${stats.winRate}%`,
      color: getWinRateColor(stats.winRate),
    },
    { label: "Pick Rate", value: `${stats.pickRate}%`, color: undefined },
    { label: "Games", value: stats.games, color: undefined },
  ];

  return (
    <div className="relative flex flex-wrap w-full items-center overflow-hidden border-b border-accent-yellow-400 pl-3 pr-0.5 py-2 gap-x-[16px] gap-y-[8px] rounded-r-[3px] bg-[linear-gradient(90deg,rgba(7,7,32,0.00)_0%,rgba(7,7,32,0.80)_100%),linear-gradient(0deg,rgba(50,115,250,0.30)_0%,rgba(50,115,250,0.00)_91.89%)] shadow-[0_2px_4px_0_rgba(0,0,0,0.40)]">
      {/* UGG Plus Overlay */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden z-[0]">
        <Icon icon={<SvgUggPlusOverlay />} className="w-[200%] -ml-[50%] h-auto min-h-full text-[#C8AC70] opacity-[0.10]" />
      </div>

      {/* Profile Icon */}
      <div className="relative z-[1] flex-shrink-0">
        <div className="flex items-center justify-center w-[38px] h-[38px] rounded-[3px] border-[1px] border-accent-yellow-400">
          <img
            className="w-[36px] h-[36px] rounded-[3px] border-[1px] border-purple-500"
            src={getProfileIconImg(stats.iconId)}
            alt="Summoner profile icon"
          />
        </div>
      </div>

      {/* Summoner Info */}
      <div className="relative z-[1] flex flex-col gap-[2px] flex-shrink-0">
        <div className="flex items-baseline gap-[4px]">
          <span className="text-[14px] font-semibold text-accent-yellow-400 leading-tight">{stats.summonerName}</span>
          <span className="text-[11px] text-accent-gray-200 leading-tight font-medium">{stats.tagLine}</span>
        </div>
        <div className="flex items-center gap-1.5 text-[11px] text-accent-gray-200 leading-tight">
          <Link
            to={`${AppRoutes.PROFILE_PLUS_TIER_LIST.replace(":region", stats.region)
              .replace(":riotUserName", stats.summonerName)
              .replace(":riotTagLine", stats.tagLine.replace("#", ""))}`}
            className={`flex items-center px-1 py-0.5 rounded-[2px] font-semibold text-[12px] ${getTierColor(stats.winRate)}`}
            style={{ backgroundColor: `${getTierColorHex(stats.winRate)}20` }}
          >
            <span className="mr-0.5">{stats.tier}</span>
            <Icon icon={<SvgArrowForward />} className="text-white w-[10px] h-[8px]" />
          </Link>
          {!noData && <LPGainArrows className="text-white font-semibold" lpAvg={stats.lp} />}
        </div>
      </div>

      {/* Stats Table / No Data Message */}
      <div className="relative z-[1] flex flex-1 min-w-[200px] ml-auto">
        {noData ? (
          <p className="flex-1 text-center" style={{ color: "#CDDCFE", fontFamily: "Inter", fontSize: "10px", fontWeight: 500 }}>
            Play at least{" "}
            <strong style={{ color: "#FFFFFF", fontFamily: "Inter", fontSize: "10px", fontWeight: 600 }}>5 games</strong>
            {" "}on this champion
            <br />
            to see your personalized stats
          </p>
        ) : (
          statValues.map((stat) => (
            <div
              key={stat.label}
              className="flex flex-col flex-1 items-center gap-[4px] border-r border-accent-purple-700 last:border-r-0"
            >
              <span className="text-[10px] text-accent-gray-100 font-medium leading-tight">{stat.label}</span>
              <span className="text-[12px] font-bold leading-tight" style={{ color: stat.color ?? "#FFFFFF" }}>
                {stat.value}
              </span>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

interface PersonalizedBuildPlusCTAProps {
  utmSource?: string;
}

export function PersonalizedBuildPlusCTA({ utmSource = "personalizedbuild" }: PersonalizedBuildPlusCTAProps) {
  const placeholderStats = [
    { label: "KDA", value: "2.33", color: undefined },
    { label: "Win Rate", value: "60%", color: getTierColorHex(60) },
    { label: "Pick Rate", value: "3.9%", color: undefined },
    { label: "Games", value: "24", color: undefined },
  ];

  return (
    <div className="relative flex flex-wrap w-full items-center overflow-hidden border-b border-accent-yellow-400 pl-3 pr-0.5 py-2 gap-x-[16px] gap-y-[8px] rounded-r-[3px] bg-[linear-gradient(90deg,rgba(7,7,32,0.00)_0%,rgba(7,7,32,0.80)_100%),linear-gradient(0deg,rgba(50,115,250,0.30)_0%,rgba(50,115,250,0.00)_91.89%)] shadow-[0_2px_4px_0_rgba(0,0,0,0.40)]">
      {/* UGG Plus Overlay */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden z-[0]">
        <Icon icon={<SvgUggPlusOverlay />} className="w-[200%] -ml-[50%] h-auto min-h-full text-[#C8AC70] opacity-[0.15]" />
      </div>

      {/* CTA Copy */}
      <div className="relative z-[1] flex flex-col gap-[4px] flex-shrink-0">
        <div className="flex items-center gap-[8px]">
          <span className="flex items-center justify-center px-[6px] py-[2px] rounded-[3px] border border-accent-yellow-400 text-[11px] font-bold text-accent-yellow-400 bg-accent-brown-400 leading-none">
            PLUS
          </span>
          <span className="text-[14px] font-semibold text-white leading-tight">Personal Stats</span>
        </div>
        <div className="text-[11px] text-lavender-50 font-medium leading-tight">
          Available now with{" "}
          <a href={`/plus?utm_source=${utmSource}`} className="text-accent-blue-400 underline">
            U.GG PLUS
          </a>
        </div>
      </div>

      {/* Blurred Placeholder Stats */}
      <div className="relative z-[1] flex flex-1 min-w-[200px] ml-auto">
        {placeholderStats.map((stat) => (
          <div
            key={stat.label}
            className="flex flex-col flex-1 items-center gap-[4px] border-r border-accent-purple-700 last:border-r-0"
          >
            <span className="text-[10px] text-accent-gray-100 font-medium leading-tight">{stat.label}</span>
            <span
              className="text-[12px] font-bold leading-tight blur-[2.5px] select-none"
              style={{ color: stat.color ?? "#FFFFFF" }}
              aria-hidden="true"
            >
              {stat.value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
